import {
  type DrillDownData,
  DrillDownType,
} from "../components/modals/DrillDownModal";
import { useDrillDownContext } from "../contexts/DrillDownContext";
import { DEMO_TRANSACTIONS } from "../data/demo-data";
import { getRecurringTransactions } from "../data/recurring-transactions-data";
import { formatDate, formatRelativeSyncTime } from "../lib/formatting";
import {
  getCashDurationShortTerm,
  getCashMovementTerms,
} from "../lib/terminology-config";

// Derive unique contributing source names for a given date window (start inclusive, end exclusive)
// Used by aggregate-level drill-downs to show provenance chips
export const getContributingSourcesForWindow = (
  windowStart: Date,
  windowEnd: Date,
): string[] => {
  const sources = new Set<string>();
  for (const txn of DEMO_TRANSACTIONS) {
    const d = new Date(txn.date);
    if (d >= windowStart && d < windowEnd && txn.dataSourceName) {
      sources.add(txn.dataSourceName);
    }
  }
  // Always include Recurring Transactions if any are active in this window
  const recurringTransactions = getRecurringTransactions();
  for (const rt of recurringTransactions) {
    if (rt.status === "Active") {
      sources.add(rt.dataSourceName);
      break; // one is enough to know manual entries contribute
    }
  }
  return Array.from(sources).sort((a, b) => {
    const aManual = a.startsWith("Manual Entry");
    const bManual = b.startsWith("Manual Entry");
    if (aManual && !bManual) return 1;
    if (!aManual && bManual) return -1;
    return a.localeCompare(b);
  });
};

// Derive unique contributing sources for a single day
export const getContributingSourcesForDay = (date: Date): string[] => {
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(end.getDate() + 1);
  return getContributingSourcesForWindow(start, end);
};

// useDrillDown is a thin consumer of DrillDownContext.
// All state lives in DrillDownProvider (rendered once in App.tsx).
// Cards call this hook exactly as before — no card-level changes needed.
export const useDrillDown = () => {
  return useDrillDownContext();
};

// ── Generator functions below — unchanged ──────────────────────────────────

// Demo overdue invoices — fixed realistic data, used by both card and drill-down modal
export const DEMO_OVERDUE_INVOICES = [
  {
    label: "INV-2025-0047 - Leicester City Council",
    value: 74432.5,
    daysOverdue: 103,
  },
  {
    label: "INV-2025-0051 - Heritage Homes Leicester",
    value: 56750.0,
    daysOverdue: 31,
  },
  {
    label: "INV-2025-0049 - Fosse Park Developments",
    value: 62200.0,
    daysOverdue: 38,
  },
  {
    label: "INV-2025-0044 - NHS Property Services",
    value: 45340.0,
    daysOverdue: 56,
  },
  {
    label: "INV-2025-0053 - Midlands Housing Group",
    value: 39825.75,
    daysOverdue: 22,
  },
  {
    label: "INV-2025-0041 - Leicester City Council",
    value: 41200.0,
    daysOverdue: 67,
  },
  {
    label: "INV-2025-0055 - University of Leicester",
    value: 38475.5,
    daysOverdue: 14,
  },
  {
    label: "INV-2025-0039 - Heritage Homes Leicester",
    value: 29340.0,
    daysOverdue: 71,
  },
  {
    label: "INV-2025-0057 - East Midlands Railway",
    value: 24650.25,
    daysOverdue: 8,
  },
  {
    label: "INV-2025-0036 - Fosse Park Developments",
    value: 19387.5,
    daysOverdue: 84,
  },
  {
    label: "INV-2025-0059 - Midlands Housing Group",
    value: 16840.0,
    daysOverdue: 5,
  },
  {
    label: "INV-2025-0033 - NHS Property Services",
    value: 14275.75,
    daysOverdue: 91,
  },
  {
    label: "INV-2025-0061 - University of Leicester",
    value: 12920.5,
    daysOverdue: 3,
  },
  {
    label: "INV-2025-0031 - East Midlands Railway",
    value: 9450.0,
    daysOverdue: 98,
  },
  {
    label: "INV-2025-0063 - Rothley Construction Partners",
    value: 7837.5,
    daysOverdue: 2,
  },
  {
    label: "INV-2025-0029 - Rothley Construction Partners",
    value: 6204.75,
    daysOverdue: 103,
  },
];
export const DEMO_OVERDUE_TOTAL = DEMO_OVERDUE_INVOICES.reduce(
  (sum, inv) => sum + inv.value,
  0,
);
export const DEMO_OVERDUE_COUNT = DEMO_OVERDUE_INVOICES.length;

// Demo upcoming bills — exactly matches the 18 currentMonthBills in financial-systems.ts
// Amounts verified: sum = £115,244.75. Due dates are relative offsets — never expire.
export const DEMO_UPCOMING_BILLS = [
  { label: "Jewson Leicester - Materials", value: 8473.25, daysUntil: 3 },
  {
    label: "Travis Perkins Leicester - Materials",
    value: 7184.5,
    daysUntil: 5,
  },
  { label: "Leicester Aggregates - Materials", value: 9847.75, daysUntil: 7 },
  { label: "Jewson Leicester - Materials", value: 6521.0, daysUntil: 9 },
  {
    label: "Midlands Roofing Supplies - Materials",
    value: 7832.5,
    daysUntil: 11,
  },
  {
    label: "Travis Perkins Leicester - Materials",
    value: 5267.25,
    daysUntil: 13,
  },
  { label: "Leicester Aggregates - Materials", value: 6043.75, daysUntil: 14 },
  { label: "Jewson Leicester - Materials", value: 4018.5, daysUntil: 16 },
  {
    label: "Sparks Electrical Ltd - Subcontractors",
    value: 8534.0,
    daysUntil: 17,
  },
  { label: "Flowtech Plumbing - Subcontractors", value: 7247.5, daysUntil: 19 },
  { label: "Heights Scaffolding Leicester", value: 9823.75, daysUntil: 20 },
  { label: "Precision Joinery Leicester", value: 6478.25, daysUntil: 21 },
  {
    label: "Sparks Electrical Ltd - Subcontractors",
    value: 5512.5,
    daysUntil: 22,
  },
  {
    label: "Flowtech Plumbing - Subcontractors",
    value: 4487.75,
    daysUntil: 23,
  },
  { label: "Midlands Plant Hire - Equipment", value: 3524.5, daysUntil: 24 },
  {
    label: "Tool Station Leicester - Equipment",
    value: 2487.25,
    daysUntil: 25,
  },
  { label: "Office Essentials Leicester", value: 6973.5, daysUntil: 27 },
  { label: "Leicester Vehicle Leasing", value: 4987.25, daysUntil: 29 },
];
export const DEMO_UPCOMING_TOTAL = DEMO_UPCOMING_BILLS.reduce(
  (sum, b) => sum + b.value,
  0,
);
export const DEMO_UPCOMING_COUNT = DEMO_UPCOMING_BILLS.length;

export const generateCashPositionDrillDown = (
  totalCash: number,
  lastSync: string | null,
): DrillDownData => {
  return {
    type: "cash-position",
    title: "Current Cash Position",
    mainValue: totalCash,
    breakdown: [
      {
        label: "Main Operating Account (HSBC)",
        value: 180000,
        subtext: "Primary operating account",
      },
      {
        label: "Payroll Account (Barclays)",
        value: 45000,
        subtext: "Dedicated payroll account",
      },
      {
        label: "Project Reserve (HSBC)",
        value: 22000,
        subtext: "Emergency reserve fund",
      },
    ],
    context: [
      { label: "Last Updated", value: formatRelativeSyncTime(lastSync) },
      { label: "Data Source", value: "Access Financials" },
    ],
  };
};

export const generateRunwayDrillDown = (
  currentCash: number,
  dailyBurn: number,
  durationMonths: number,
): DrillDownData => {
  const durationDays = Math.floor(currentCash / dailyBurn);
  const runoutDate = new Date(Date.now() + durationDays * 24 * 60 * 60 * 1000);
  const durationTerm = getCashDurationShortTerm();
  const durationTermCapitalized =
    durationTerm.charAt(0).toUpperCase() + durationTerm.slice(1);

  return {
    type: "runway",
    title: `Cash ${durationTermCapitalized} Analysis`,
    mainValue: currentCash,
    calculation: {
      formula: `${durationTermCapitalized} = Current Cash ÷ Daily Burn Rate`,
      steps: [
        { label: "Current Cash", value: currentCash },
        { label: "Daily Burn Rate", value: dailyBurn },
        {
          label: `${durationTermCapitalized} (days)`,
          value: `${durationDays} days`,
        },
        {
          label: `${durationTermCapitalized} (months)`,
          value: `${durationMonths} months`,
        },
        {
          label: "Cash Runs Out",
          value: formatDate(runoutDate, undefined, {
            month: "short",
            day: "numeric",
            year: "numeric",
          }),
        },
      ],
    },
    context: [
      {
        label: "Status",
        value:
          durationMonths >= 6
            ? "✓ Healthy"
            : durationMonths >= 3
              ? "⚠ Caution"
              : "⚠ Critical",
      },
      {
        label: "Recommendation",
        value:
          durationMonths < 6
            ? "Increase revenue or reduce costs"
            : "Maintain current trajectory",
      },
    ],
  };
};

export const generateRevenueDrillDown = (
  totalRevenue: number,
): DrillDownData => {
  return {
    type: "revenue",
    title: "Expected Income (Next 30 Days)",
    mainValue: totalRevenue,
    breakdown: [
      {
        label: "Invoice #1245 - BigCo Ltd",
        value: 35000,
        subtext: "Due in 5 days • Certain",
      },
      {
        label: "Invoice #1250 - TechStart Ltd",
        value: 18000,
        subtext: "Due in 10 days • Probable",
      },
      {
        label: "Invoice #1258 - SmallCo",
        value: 7800,
        subtext: "Due in 12 days • Certain",
      },
      {
        label: "Invoice #1260 - MegaCorp",
        value: 45000,
        subtext: "Due in 25 days • Certain",
      },
      {
        label: "Invoice #1262 - ClientCo",
        value: 12000,
        subtext: "Due in 30 days • Probable",
      },
      {
        label: "Overdue Collections",
        value: 27700,
        subtext: "3 overdue invoices",
      },
    ],
    context: [
      { label: "Total Invoices", value: "6 invoices" },
      { label: "Confidence Level", value: "85% weighted average" },
    ],
  };
};

export const generateExpensesDrillDown = (
  totalExpenses: number,
): DrillDownData => {
  const movementTerms = getCashMovementTerms();
  return {
    type: "expenses",
    title: `Expected ${movementTerms.out} (Next 30 Days)`,
    mainValue: totalExpenses,
    breakdown: [
      {
        label: "Payroll - Biweekly Runs",
        value: 90000,
        subtext: "2 payroll runs • 87 employees",
      },
      {
        label: "Supplier Payments",
        value: 55500,
        subtext: "5 bills due",
      },
      {
        label: "VAT Payment",
        value: 15000,
        subtext: "Due in 28 days",
      },
      {
        label: "Fixed Costs",
        value: 7800,
        subtext: "Rent, insurance, utilities",
      },
      {
        label: "Operating Expenses",
        value: 12000,
        subtext: "Daily operations",
      },
    ],
    context: [
      { label: "Total Items", value: "5 categories" },
      { label: "Largest Item", value: "Payroll (50%)" },
    ],
  };
};

export interface DateBreakdownTransaction {
  name: string;
  description: string | null;
  grossValueIn: number;
  grossValueOut: number;
  category: string | null;
  dataSourceName?: string;
}

export const generateDateBreakdownDrillDown = (
  date: string,
  balance: number,
  incoming: number,
  outgoing: number,
  dateObj?: Date,
  scenarioName?: string,
  actualTransactions?: DateBreakdownTransaction[],
): DrillDownData => {
  // Derive contributing sources for this specific day
  const sources = dateObj ? getContributingSourcesForDay(dateObj) : [];
  const movementTerms = getCashMovementTerms();
  const openingBalance = balance - incoming + outgoing;
  const isLiveMode = actualTransactions !== undefined;

  const incomeItems: Array<{ label: string; value: number; subtext?: string }> =
    [];
  const expenseItems: Array<{
    label: string;
    value: number;
    subtext?: string;
  }> = [];

  if (isLiveMode) {
    // Live mode: build breakdown from actual transactions
    for (const txn of actualTransactions) {
      const label = txn.description
        ? `${txn.name} - ${txn.description}`
        : txn.name;
      if (txn.grossValueIn !== 0) {
        incomeItems.push({
          label,
          value: txn.grossValueIn,
          subtext: txn.dataSourceName ?? txn.category ?? undefined,
        });
      }
      if (txn.grossValueOut !== 0) {
        expenseItems.push({
          label,
          value: txn.grossValueOut,
          subtext: txn.dataSourceName ?? txn.category ?? undefined,
        });
      }
    }

    // Live-mode sources come from the actual transactions
    const liveSources = new Set<string>();
    for (const txn of actualTransactions) {
      if (txn.dataSourceName) liveSources.add(txn.dataSourceName);
    }
    sources.length = 0;
    if (liveSources.size > 0) {
      sources.push(...Array.from(liveSources).sort());
    }
  } else {
    // Demo mode: generate realistic transaction breakdown based on amounts
    if (incoming > 0) {
      if (incoming >= 40000) {
        incomeItems.push({
          label: "Invoice #1260 - MegaCorp",
          value: 45000,
          subtext: "Project milestone payment",
        });
      } else if (incoming >= 30000) {
        incomeItems.push({
          label: "Invoice #1245 - BigCo Ltd",
          value: 35000,
          subtext: "Monthly retainer",
        });
      } else if (incoming >= 20000) {
        incomeItems.push({
          label: "Invoice #1254 - TechStart Ltd",
          value: 22000,
          subtext: "Project completion",
        });
      } else if (incoming >= 15000) {
        incomeItems.push({
          label: "Invoice #1250 - TechStart Ltd",
          value: 18000,
          subtext: "Monthly service",
        });
      } else if (incoming >= 10000) {
        incomeItems.push({
          label: "Invoice #1262 - ClientCo",
          value: 12000,
          subtext: "Consulting fees",
        });
      } else if (incoming >= 5000) {
        incomeItems.push({
          label: "Invoice #1258 - SmallCo",
          value: 7800,
          subtext: "Project deposit",
        });
      } else if (incoming > 1000) {
        incomeItems.push({
          label: `Daily ${movementTerms.in}`,
          value: incoming,
          subtext: "Small payments and deposits",
        });
      }
    }

    if (outgoing > 0) {
      if (outgoing >= 60000) {
        expenseItems.push({
          label: "Payroll - Biweekly Run",
          value: 45000,
          subtext: "87 employees",
        });
        const remaining = outgoing - 45000;
        if (remaining >= 15000) {
          expenseItems.push({
            label: "VAT Payment - HMRC",
            value: 15000,
            subtext: "Quarterly VAT",
          });
        }
        if (remaining > 3000 && remaining < 15000) {
          expenseItems.push({
            label: "Jewson - Materials",
            value: Math.round(remaining / 2),
            subtext: "Construction materials",
          });
          expenseItems.push({
            label: "Operating Expenses",
            value: Math.round(remaining / 2),
            subtext: "Daily costs",
          });
        }
      } else if (outgoing >= 40000) {
        expenseItems.push({
          label: "Payroll - Biweekly Run",
          value: 45000,
          subtext: "87 employees",
        });
      } else if (outgoing >= 20000) {
        expenseItems.push({
          label: "ABC Subcontractors",
          value: 25000,
          subtext: "Subcontractor payment",
        });
      } else if (outgoing >= 15000) {
        expenseItems.push({
          label: "BuildSupply Co",
          value: 18000,
          subtext: "Materials delivery",
        });
      } else if (outgoing >= 10000) {
        expenseItems.push({
          label: "Travis Perkins",
          value: 12500,
          subtext: "Building supplies",
        });
      } else if (outgoing >= 7000) {
        expenseItems.push({
          label: "XYZ Equipment Hire",
          value: 8000,
          subtext: "Equipment rental",
        });
        expenseItems.push({
          label: "Fixed Costs",
          value: outgoing - 8000,
          subtext: "Rent, utilities",
        });
      } else if (outgoing >= 3000) {
        expenseItems.push({
          label: "Operating Expenses",
          value: outgoing,
          subtext: "Daily business costs",
        });
      } else {
        expenseItems.push({
          label: "Minimal Costs",
          value: outgoing,
          subtext: "Weekend/holiday operations",
        });
      }
    }
  }

  return {
    type: "forecast-point",
    title: scenarioName
      ? `Cash Flow Breakdown — ${scenarioName}`
      : `Cash Flow Breakdown`,
    date: date,
    mainValue: balance,
    sources: sources.length > 0 ? sources : undefined,
    breakdown: [
      {
        label: "Opening Balance",
        value: openingBalance,
        subtext: "Start of day",
      },
      ...incomeItems.map((item) => ({
        ...item,
        label: item.label,
      })),
      ...expenseItems.map((item) => ({
        ...item,
        value: -item.value,
        label: item.label,
      })),
      {
        label: "Closing Balance",
        value: balance,
        subtext: "End of day",
      },
    ],
    context: [
      { label: `Total ${movementTerms.in}`, value: incoming },
      { label: `Total ${movementTerms.out}`, value: outgoing },
      { label: "Net Change", value: incoming - outgoing },
      ...(scenarioName ? [{ label: "Scenario", value: scenarioName }] : []),
      ...(isLiveMode
        ? [{ label: "Data Source", value: "Live transactions" }]
        : []),
    ],
  };
};

export const generateVarianceDrillDown = (
  forecasted: number,
  actual: number,
  variance: number,
  variancePercent: number,
  period: "week" | "month" = "week",
): DrillDownData => {
  const periodLabel = period === "week" ? "This Week" : "This Month";
  return {
    type: "comparison-cell",
    title: `Variance Analysis - ${periodLabel}`,
    mainValue: variance,
    calculation: {
      formula: "Variance = Actual - Forecasted",
      steps: [
        { label: "Forecasted Cash", value: forecasted },
        { label: "Actual Cash", value: actual },
        { label: "Variance Amount", value: variance },
        { label: "Variance %", value: `${variancePercent.toFixed(1)}%` },
      ],
    },
    breakdown: [
      {
        label: "SmallCo Invoice #1198",
        value: -8500,
        subtext: "Expected Nov 20, received Nov 28 (8 days late)",
      },
      {
        label: "BigCo Invoice #1234",
        value: 0,
        subtext: "Expected Nov 18, received Nov 18 (On time ✓)",
      },
      {
        label: "Unexpected Equipment Repair",
        value: -2800,
        subtext: "Unplanned expense",
      },
      {
        label: "Early Payment Discount",
        value: 3300,
        subtext: "Saved 3% on supplier payment",
      },
    ],
    context: [
      {
        label: "Forecast Accuracy",
        value: `${(100 + variancePercent).toFixed(1)}%`,
      },
      {
        label: "Status",
        value:
          Math.abs(variancePercent) < 5
            ? "✓ Within tolerance"
            : "⚠ Review needed",
      },
    ],
  };
};

export const generateAccountDrillDown = (
  accountName: string,
  balance: number,
  accountId: string,
): DrillDownData => {
  // Generate recent transactions for this account
  const transactions = [
    {
      date: formatDate(new Date(Date.now() - 2 * 24 * 60 * 60 * 1000)),
      description: "Materials - Jewson",
      amount: -5200,
      category: "Materials",
    },
    {
      date: formatDate(new Date(Date.now() - 5 * 24 * 60 * 60 * 1000)),
      description: "Payment from ClientCo",
      amount: 15000,
      category: "Income",
    },
    {
      date: formatDate(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)),
      description: "Payroll - Biweekly",
      amount: -45000,
      category: "Payroll",
    },
    {
      date: formatDate(new Date(Date.now() - 10 * 24 * 60 * 60 * 1000)),
      description: "Subcontractor - ABC Ltd",
      amount: -12000,
      category: "Subcontractors",
    },
    {
      date: formatDate(new Date(Date.now() - 12 * 24 * 60 * 60 * 1000)),
      description: "Invoice Payment - BigCo",
      amount: 35000,
      category: "Income",
    },
  ];

  return {
    type: "cash-position",
    title: accountName,
    mainValue: balance,
    transactions,
    context: [
      {
        label: "Account Type",
        value: accountId.includes("operating")
          ? "Current Account"
          : accountId.includes("payroll")
            ? "Payroll Account"
            : "Savings Account",
      },
      { label: "Last Transaction", value: transactions[0].date },
      { label: "Data Source", value: "Access Financials" },
    ],
    actions: [
      {
        label: "View All Transactions",
        onClick: () => console.log("View all transactions for", accountId),
      },
      {
        label: "View in Access Financials",
        onClick: () => console.log("Open Access Financials for", accountId),
      },
    ],
  };
};

export const generateInvoiceDrillDown = (invoice: any): DrillDownData => {
  return {
    type: "revenue",
    title: `Invoice ${invoice.invoiceNumber}`,
    mainValue: invoice.amount,
    breakdown: [
      {
        label: "Customer",
        value: 0,
        subtext: invoice.customer,
      },
      {
        label: "Invoice Date",
        value: 0,
        subtext: formatDate(new Date(invoice.date)),
      },
      {
        label: "Due Date",
        value: 0,
        subtext: formatDate(new Date(invoice.dueDate)),
      },
      {
        label: "Status",
        value: 0,
        subtext:
          invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1),
      },
    ],
    context: [
      {
        label: "Days Outstanding",
        value: Math.floor(
          (Date.now() - new Date(invoice.dueDate).getTime()) /
            (1000 * 60 * 60 * 24),
        ),
      },
      { label: "Payment Terms", value: "30 days" },
      { label: "Confidence", value: invoice.confidence || "High" },
    ],
    actions: [
      {
        label: "Send Reminder",
        onClick: () => console.log("Send reminder for", invoice.id),
      },
      {
        label: "View in Access Financials",
        onClick: () => console.log("Open invoice", invoice.id),
      },
    ],
  };
};

export const generate30DayForecastDrillDown = (
  forecastAmount: number,
  currentCash: number,
  expectedIncome: number,
  expectedExpenses: number,
  actualIncome?: ForecastIncomeData[],
  actualExpenses?: ForecastExpenseData[],
): DrillDownData => {
  const netChange = expectedIncome - expectedExpenses;
  const isLiveMode = actualIncome !== undefined && actualExpenses !== undefined;

  let breakdown: Array<{ label: string; value: number; subtext?: string }>;

  if (isLiveMode) {
    // Live mode: show actual transactions grouped by income and expenses
    const incomeItems = actualIncome.map((item) => {
      const label = item.description
        ? `${item.customer} - ${item.description}`
        : item.customer;
      const dueDate = new Date(item.dueDate);
      const daysUntilDue = Math.ceil(
        (dueDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24),
      );
      return {
        label,
        value: item.outstandingValue,
        subtext:
          daysUntilDue <= 0
            ? "Due today"
            : `Due in ${daysUntilDue} day${daysUntilDue !== 1 ? "s" : ""}`,
      };
    });

    const expenseItems = actualExpenses.map((item) => {
      const label = item.description
        ? `${item.supplier} - ${item.description}`
        : item.supplier;
      const dueDate = new Date(item.dueDate);
      const daysUntilDue = Math.ceil(
        (dueDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24),
      );
      return {
        label,
        value: -item.outstandingValue,
        subtext:
          daysUntilDue <= 0
            ? "Due today"
            : `Due in ${daysUntilDue} day${daysUntilDue !== 1 ? "s" : ""}`,
      };
    });

    // Build breakdown with summary rows first, then individual transactions
    breakdown = [
      {
        label: "Expected Income",
        value: expectedIncome,
        subtext: `${actualIncome.length} invoice${actualIncome.length !== 1 ? "s" : ""} due`,
      },
      ...incomeItems,
      {
        label: "Expected Expenses",
        value: -expectedExpenses,
        subtext: `${actualExpenses.length} payment${actualExpenses.length !== 1 ? "s" : ""} due`,
      },
      ...expenseItems,
      {
        label: "Net Change",
        value: netChange,
        subtext: netChange >= 0 ? "Positive cash flow" : "Negative cash flow",
      },
    ];
  } else {
    // Demo mode: use sample data
    breakdown = [
      {
        label: "Expected Income",
        value: expectedIncome,
        subtext: "12 invoices due",
      },
      {
        label: "Expected Expenses",
        value: -expectedExpenses,
        subtext: "2 payroll runs + suppliers",
      },
      {
        label: "Net Change",
        value: netChange,
        subtext: netChange >= 0 ? "Positive cash flow" : "Negative cash flow",
      },
    ];
  }

  return {
    type: "forecast-point",
    title: "30-Day Cash Forecast",
    date: formatDate(
      new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
      undefined,
      { month: "long", day: "numeric", year: "numeric" },
    ),
    mainValue: forecastAmount,
    calculation: {
      formula: "Forecast = Current Cash + Expected Income - Expected Expenses",
      steps: [
        { label: "Current Cash", value: currentCash },
        { label: "Expected Income (30 days)", value: expectedIncome },
        { label: "Expected Expenses (30 days)", value: expectedExpenses },
        { label: "Net Change", value: netChange },
        { label: "Forecasted Cash", value: forecastAmount },
      ],
    },
    breakdown,
    context: isLiveMode
      ? [
          {
            label: "Income Items",
            value: `${actualIncome.length} invoice${actualIncome.length !== 1 ? "s" : ""}`,
          },
          {
            label: "Expense Items",
            value: `${actualExpenses.length} payment${actualExpenses.length !== 1 ? "s" : ""}`,
          },
          { label: "Data Source", value: "Live transactions" },
          {
            label: "Risk Level",
            value:
              forecastAmount < 50000
                ? "High"
                : forecastAmount < 100000
                  ? "Medium"
                  : "Low",
          },
        ]
      : [
          { label: "Confidence Level", value: "82%" },
          {
            label: "Based On",
            value: "Historical patterns + scheduled transactions",
          },
          {
            label: "Risk Level",
            value:
              forecastAmount < 50000
                ? "High"
                : forecastAmount < 100000
                  ? "Medium"
                  : "Low",
          },
        ],
  };
};

export const generate90DayForecastDrillDown = (
  forecastAmount: number,
  currentCash: number,
  expectedIncome: number,
  expectedExpenses: number,
  actualIncome?: ForecastIncomeData[],
  actualExpenses?: ForecastExpenseData[],
): DrillDownData => {
  const netChange = expectedIncome - expectedExpenses;
  const isLiveMode = actualIncome !== undefined && actualExpenses !== undefined;

  let breakdown: Array<{ label: string; value: number; subtext?: string }>;

  if (isLiveMode) {
    // Live mode: show actual transactions grouped by income and expenses
    const incomeItems = actualIncome.map((item) => {
      const label = item.description
        ? `${item.customer} - ${item.description}`
        : item.customer;
      const dueDate = new Date(item.dueDate);
      const daysUntilDue = Math.ceil(
        (dueDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24),
      );
      return {
        label,
        value: item.outstandingValue,
        subtext:
          daysUntilDue <= 0
            ? "Due today"
            : `Due in ${daysUntilDue} day${daysUntilDue !== 1 ? "s" : ""}`,
      };
    });

    const expenseItems = actualExpenses.map((item) => {
      const label = item.description
        ? `${item.supplier} - ${item.description}`
        : item.supplier;
      const dueDate = new Date(item.dueDate);
      const daysUntilDue = Math.ceil(
        (dueDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24),
      );
      return {
        label,
        value: -item.outstandingValue,
        subtext:
          daysUntilDue <= 0
            ? "Due today"
            : `Due in ${daysUntilDue} day${daysUntilDue !== 1 ? "s" : ""}`,
      };
    });

    // Build breakdown with summary rows first, then individual transactions
    breakdown = [
      {
        label: "Expected Income",
        value: expectedIncome,
        subtext: `${actualIncome.length} invoice${actualIncome.length !== 1 ? "s" : ""} due`,
      },
      ...incomeItems,
      {
        label: "Expected Expenses",
        value: -expectedExpenses,
        subtext: `${actualExpenses.length} payment${actualExpenses.length !== 1 ? "s" : ""} due`,
      },
      ...expenseItems,
      {
        label: "Net Change",
        value: netChange,
        subtext: netChange >= 0 ? "Positive cash flow" : "Negative cash flow",
      },
    ];
  } else {
    // Demo mode: use sample data
    breakdown = [
      {
        label: "Expected Income",
        value: expectedIncome,
        subtext: "35+ invoices due",
      },
      {
        label: "Expected Expenses",
        value: -expectedExpenses,
        subtext: "6 payroll runs + suppliers",
      },
      {
        label: "Net Change",
        value: netChange,
        subtext: netChange >= 0 ? "Positive cash flow" : "Negative cash flow",
      },
    ];
  }

  return {
    type: "forecast-point",
    title: "90-Day Cash Forecast",
    date: formatDate(
      new Date(Date.now() + 90 * 24 * 60 * 60 * 1000),
      undefined,
      { month: "long", day: "numeric", year: "numeric" },
    ),
    mainValue: forecastAmount,
    calculation: {
      formula: "Forecast = Current Cash + Expected Income - Expected Expenses",
      steps: [
        { label: "Current Cash", value: currentCash },
        { label: "Expected Income (90 days)", value: expectedIncome },
        { label: "Expected Expenses (90 days)", value: expectedExpenses },
        { label: "Net Change", value: netChange },
        { label: "Forecasted Cash", value: forecastAmount },
      ],
    },
    breakdown,
    context: isLiveMode
      ? [
          {
            label: "Income Items",
            value: `${actualIncome.length} invoice${actualIncome.length !== 1 ? "s" : ""}`,
          },
          {
            label: "Expense Items",
            value: `${actualExpenses.length} payment${actualExpenses.length !== 1 ? "s" : ""}`,
          },
          { label: "Data Source", value: "Live transactions" },
          {
            label: "Risk Level",
            value:
              forecastAmount < 50000
                ? "High"
                : forecastAmount < 100000
                  ? "Medium"
                  : "Low",
          },
        ]
      : [
          { label: "Confidence Level", value: "68%" },
          {
            label: "Based On",
            value: "Historical patterns + scheduled transactions",
          },
          {
            label: "Risk Level",
            value:
              forecastAmount < 50000
                ? "High"
                : forecastAmount < 100000
                  ? "Medium"
                  : "Low",
          },
        ],
  };
};

export interface OverdueInvoiceData {
  customer: string;
  description: string | null;
  dueDate: string;
  outstandingValue: number;
}

export interface UpcomingPaymentData {
  supplier: string;
  description: string | null;
  dueDate: string;
  outstandingValue: number;
}

export interface ForecastIncomeData {
  customer: string;
  description: string | null;
  dueDate: string;
  outstandingValue: number;
}

export interface ForecastExpenseData {
  supplier: string;
  description: string | null;
  dueDate: string;
  outstandingValue: number;
}

export const generateOverdueInvoicesDrillDown = (
  totalAmount: number,
  count: number,
  actualInvoices?: OverdueInvoiceData[],
): DrillDownData => {
  const now = new Date();

  // If actualInvoices is provided (even if empty), we're in live mode - don't use sample data
  const isLiveMode = actualInvoices !== undefined;

  let breakdown: Array<{ label: string; value: number; subtext?: string }>;
  let oldestDaysOverdue = 0;
  let totalDaysOverdue = 0;

  if (isLiveMode) {
    // Live mode: use actual invoices from database
    if (actualInvoices.length > 0) {
      // Use slice() before sort() to avoid mutating the input array (React state)
      breakdown = actualInvoices
        .slice()
        .sort(
          (a, b) =>
            new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime(),
        ) // Sort by due date (oldest first)
        .map((invoice) => {
          const dueDate = new Date(invoice.dueDate);
          const daysOverdue = Math.floor(
            (now.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24),
          );
          totalDaysOverdue += daysOverdue;
          if (daysOverdue > oldestDaysOverdue) {
            oldestDaysOverdue = daysOverdue;
          }
          const label = invoice.description
            ? `${invoice.customer} - ${invoice.description}`
            : invoice.customer;
          return {
            label,
            value: invoice.outstandingValue,
            subtext: `${daysOverdue} days overdue`,
          };
        });
    } else {
      // Live mode but no overdue invoices
      breakdown = [];
    }
  } else {
    // Demo mode: fixed realistic invoices, days overdue relative to today
    breakdown = DEMO_OVERDUE_INVOICES.sort(
      (a, b) => b.daysOverdue - a.daysOverdue,
    ) // Oldest first
      .map((inv) => {
        totalDaysOverdue += inv.daysOverdue;
        if (inv.daysOverdue > oldestDaysOverdue)
          oldestDaysOverdue = inv.daysOverdue;
        return {
          label: inv.label,
          value: inv.value,
          subtext: `${inv.daysOverdue} days overdue`,
        };
      });
    // Use the actual sum of demo invoices as the total — ensures card and modal match
    totalAmount = DEMO_OVERDUE_INVOICES.reduce(
      (sum, inv) => sum + inv.value,
      0,
    );
    count = DEMO_OVERDUE_INVOICES.length;
  }

  const avgDaysOverdue = count > 0 ? Math.round(totalDaysOverdue / count) : 0;

  return {
    type: "revenue",
    title: "Overdue Invoices",
    mainValue: totalAmount,
    breakdown,
    context: [
      {
        label: "Total Invoices",
        value: `${count} invoice${count !== 1 ? "s" : ""}`,
      },
      {
        label: "Average Days Overdue",
        value: avgDaysOverdue > 0 ? `${avgDaysOverdue} days` : "N/A",
      },
      {
        label: "Oldest Invoice",
        value:
          oldestDaysOverdue > 0 ? `${oldestDaysOverdue} days overdue` : "N/A",
      },
      { label: "Total Outstanding", value: totalAmount },
    ],
  };
};

export const generateUpcomingPaymentsDrillDown = (
  totalAmount: number,
  count: number,
  actualPayments?: UpcomingPaymentData[],
): DrillDownData => {
  const now = new Date();

  // If actualPayments is provided (even if empty), we're in live mode - don't use sample data
  const isLiveMode = actualPayments !== undefined;

  let breakdown: Array<{ label: string; value: number; subtext?: string }>;
  let soonestDaysUntilDue = 0;
  let totalDaysUntilDue = 0;
  let largestPayment = { label: "", value: 0 };

  if (isLiveMode) {
    // Live mode: use actual payments from database
    if (actualPayments.length > 0) {
      // Use slice() before sort() to avoid mutating the input array (React state)
      breakdown = actualPayments
        .slice()
        .sort(
          (a, b) =>
            new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime(),
        ) // Sort by due date (soonest first)
        .map((payment) => {
          const dueDate = new Date(payment.dueDate);
          const daysUntilDue = Math.ceil(
            (dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
          );
          totalDaysUntilDue += daysUntilDue;
          if (soonestDaysUntilDue === 0 || daysUntilDue < soonestDaysUntilDue) {
            soonestDaysUntilDue = daysUntilDue;
          }
          if (payment.outstandingValue > largestPayment.value) {
            largestPayment = {
              label: payment.supplier,
              value: payment.outstandingValue,
            };
          }
          const label = payment.description
            ? `${payment.supplier} - ${payment.description}`
            : payment.supplier;
          return {
            label,
            value: payment.outstandingValue,
            subtext:
              daysUntilDue <= 0
                ? "Due today"
                : `Due in ${daysUntilDue} day${daysUntilDue !== 1 ? "s" : ""}`,
          };
        });
    } else {
      // Live mode but no upcoming payments
      breakdown = [];
    }
  } else {
    // Demo mode: use DEMO_UPCOMING_BILLS — matches financial-systems.ts exactly
    breakdown = [...DEMO_UPCOMING_BILLS]
      .sort((a, b) => a.daysUntil - b.daysUntil)
      .map((p) => ({
        label: p.label,
        value: p.value,
        subtext:
          p.daysUntil === 0
            ? "Due today"
            : `Due in ${p.daysUntil} day${p.daysUntil !== 1 ? "s" : ""}`,
      }));

    soonestDaysUntilDue = Math.min(
      ...DEMO_UPCOMING_BILLS.map((p) => p.daysUntil),
    );
    totalDaysUntilDue = DEMO_UPCOMING_BILLS.reduce(
      (sum, p) => sum + p.daysUntil,
      0,
    );
    count = DEMO_UPCOMING_COUNT;
    totalAmount = DEMO_UPCOMING_TOTAL;
  }

  const avgDaysUntilDue = count > 0 ? Math.round(totalDaysUntilDue / count) : 0;

  return {
    type: "expenses",
    title: "Upcoming Payments (Next 30 Days)",
    mainValue: totalAmount,
    breakdown,
    context: [
      {
        label: "Total Payments",
        value: `${count} payment${count !== 1 ? "s" : ""}`,
      },
      {
        label: "Average Days Until Due",
        value: avgDaysUntilDue > 0 ? `${avgDaysUntilDue} days` : "N/A",
      },
      {
        label: "Soonest Payment",
        value:
          soonestDaysUntilDue > 0
            ? `${soonestDaysUntilDue} days`
            : count > 0
              ? "Due today"
              : "N/A",
      },
      { label: "Total Upcoming", value: totalAmount },
    ],
  };
};
